import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import FormControl, { TextFieldControl } from "components/FormControl";
import Loader from "components/Layout/Loader";
import LoadingButton from "components/LoadingButton";
import PublishStatus from "components/PublishStatus";
import Row from "components/Form/Row";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import TriviaStore from "../../../TriviaStore";

import {
  compose,
  fileSizeValidator,
  imageTypesValidator,
  lengthValidator,
} from "utils/validators";

import {
  Container,
  TitleContainer,
  Buttons,
  ButtonContainer,
  UploadPreviewFieldControl,
} from "./styles";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import TagDictionary from "stores/dictionary/TagDictionary";

const MAX_FILE_SIZE_IN_MB = 10;

const OptionForm = ({
  formTitle,
  initialValues = {},
  isEditMode,
  formData,
  onClose,
  isLoading,
  questionId,
}: any) => {
  const onSubmit = (values: any) => {
    try {
      const data = {
        title: values.title.trim(),
        code: values.code.trim(),
        tagsIds: values.tag.map((tag: any) => tag.id),
        displayOrder: 0,
        questionID: questionId,
      };
      TriviaStore.addAnswerOption(data);
    } catch (error) {
    } finally {
    }
  };
  const onChangeIsDirty = () => {};

  useEffect(() => {
    Promise.all([TagDictionary.fetch()]);
  }, []);

  return (
    <FormControl onSubmit={onSubmit} initialValues={initialValues}>
      <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />

      <Container>
        <TitleContainer>
          <Typography variant="h1" color="textPrimary">
            {formTitle}
          </Typography>
          {isEditMode && formData.publishStatus && (
            <PublishStatus status={formData.publishStatus} />
          )}
        </TitleContainer>
        <Form
          isFormLoading={false}
          formData={{}}
          tagOptions={TagDictionary.options}
        />

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl
              render={({ onClick }: { onClick: () => void }) => (
                <LoadingButton
                  onClick={onClick}
                  variant="contained"
                  color="primary"
                  isLoading={isLoading}
                  fullWidth
                >
                  {!isEditMode ? "Add" : "Save"}
                </LoadingButton>
              )}
            />
          </ButtonContainer>
        </Buttons>
      </Container>
    </FormControl>
  );
};

export default observer(OptionForm);

const Form = ({ isFormLoading = false, formData, tagOptions }: any) => {
  const formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  if (isFormLoading) {
    return (
      <Row>
        <Loader />
      </Row>
    );
  }

  return (
    <>
      <Row>
        <TextFieldControl
          variant="outlined"
          margin="none"
          label="Title"
          name="title"
          value={formData?.title}
          placeholder="Enter title"
          validate={lengthValidator(128)}
          fullWidth
          required
        />
      </Row>
      <Row>
        <TextFieldControl
          variant="outlined"
          margin="none"
          label="Code"
          name="code"
          value={formData?.code}
          placeholder="Enter code"
          validate={lengthValidator(32)}
          fullWidth
          required
        />
      </Row>
      <Row>
        <DropdownFieldControl
          name="tag"
          options={tagOptions}
          label="Tag"
          multi
          isClearable
        />
      </Row>
      <Row>
        <UploadPreviewFieldControl
          title="Icon"
          variant="outlined"
          margin="none"
          name="file"
          acceptedFiles={ACCEPTED_IMAGE_TYPES}
          validate={compose(
            formFileValidator(ACCEPTED_IMAGE_TYPES),
            fileSizeValidator(MAX_FILE_SIZE_IN_MB),
          )}
          required={false}
        />
      </Row>
    </>
  );
};
